// Where the user is in the handover screens, kept in the URL hash (see routing.ts):
// #handover/<project>[/a/<apartment>][/h/<handover>[/v/<step>[/<room>]]]. Only ids, never data.
// The step is the part of an open handover shown on a phone (its rooms, one room, the summary…).

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export interface HandoverLocation {
  readonly projectId: string | null;
  readonly apartmentId: string | null;
  readonly handoverId: string | null;
}

export const EMPTY_LOCATION: HandoverLocation = {
  projectId: null,
  apartmentId: null,
  handoverId: null,
};

/** The location in a hash such as "#handover/<p>/a/<apt>/h/<id>"; unknown parts are ignored. */
export function parseLocation(hash: string): HandoverLocation {
  const parts = hash.replace(/^#/, '').split('/');
  if (parts[0] !== 'handover') return EMPTY_LOCATION;
  const projectId = parts[1] !== undefined && UUID.test(parts[1]) ? parts[1] : null;
  const after = (marker: string): string | null => {
    const index = parts.indexOf(marker, 2);
    const value = index < 0 ? undefined : parts[index + 1];
    return value !== undefined && UUID.test(value) ? value : null;
  };
  return projectId === null
    ? EMPTY_LOCATION
    : { projectId, apartmentId: after('a'), handoverId: after('h') };
}

export function formatLocation(location: HandoverLocation): string {
  if (location.projectId === null) return '#handover';
  return [
    '#handover',
    location.projectId,
    ...(location.apartmentId === null ? [] : ['a', location.apartmentId]),
    ...(location.handoverId === null ? [] : ['h', location.handoverId]),
  ].join('/');
}

/** The part of an open handover shown on a phone. */
export type HandoverStep =
  | { readonly kind: 'overview' }
  | { readonly kind: 'room'; readonly areaId: string }
  | { readonly kind: 'checks' | 'reinspection' | 'findings' | 'review' };

export const OVERVIEW: HandoverStep = { kind: 'overview' };

const STEPS = ['checks', 'reinspection', 'findings', 'review'] as const;

/** The step in a handover hash ("…/h/<id>/v/room/<area>"); the overview when absent or unknown. */
export function parseStep(hash: string): HandoverStep {
  const parts = hash.replace(/^#/, '').split('/');
  const index = parts.indexOf('v', 2);
  if (parts[0] !== 'handover' || index < 0) return OVERVIEW;
  const kind = parts[index + 1];
  const id = parts[index + 2];
  if (kind === 'room') return id !== undefined && UUID.test(id) ? { kind, areaId: id } : OVERVIEW;
  const step = STEPS.find((s) => s === kind);
  return step === undefined ? OVERVIEW : { kind: step };
}

/** The hash of a step of the open handover in `location`. */
export function formatStep(location: HandoverLocation, step: HandoverStep): string {
  const base = formatLocation(location);
  if (location.handoverId === null || step.kind === 'overview') return base;
  return step.kind === 'room' ? `${base}/v/room/${step.areaId}` : `${base}/v/${step.kind}`;
}
